import { faEthereum } from '@fortawesome/free-brands-svg-icons';
import { faRightLeft } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, defineMessage } from 'react-intl';
import styled from 'styled-components';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useFiatBalance } from '@sorare/core/src/hooks/wallets/useFiatBalance';
import { getFaCurrencySymbol } from '@sorare/core/src/lib/fiat';

import { usePaymentContext } from 'components/buyActions/Context';

const anonymousSeller = defineMessage({
  id: 'NewPaymentBox.methods.anonymousSeller',
  defaultMessage: 'The seller',
});

const Banner = styled(Horizontal).attrs({ center: true })`
  justify-content: center;
  padding: var(--intermediate-unit);
  border-radius: var(--unit);
  background: rgba(255, 255, 255, 0.11);
`;

const StyledTabBar = styled(TabBar)`
  --active-fg-color: var(--c-neutral-1000);
  --active-bg-color: var(--c-neutral-400);

  &.fullWidth {
    --tab-padding-y: var(--unit);
  }
`;

const CurrencyTabs = () => {
  const { formatMessage } = useIntlContext();
  const { fiatCurrency } = useFiatBalance();
  const {
    walletPreferences: { onlyShowFiatCurrency },
  } = useCurrentUserContext();
  const { paymentCurrency, currencies, setPaymentCurrency, seller } =
    usePaymentContext();

  const fiatPaymentEnabled = currencies.includes(Currency.FIAT);

  if (onlyShowFiatCurrency && fiatPaymentEnabled) return null;

  return (
    <Vertical>
      {currencies.length === 1 && (
        <Banner>
          <FontAwesomeIcon icon={faRightLeft} size="sm" />
          <LabelM bold>
            {fiatPaymentEnabled ? (
              <FormattedMessage
                id="CurrencyTabs.singleCurrencyAccepted.fiat"
                defaultMessage="{seller} only accept cash."
                values={{
                  seller: seller || formatMessage(anonymousSeller),
                }}
              />
            ) : (
              <FormattedMessage
                id="CurrencyTabs.singleCurrencyAccepted.eth"
                defaultMessage="{seller} only accept ETH."
                values={{
                  seller: seller || formatMessage(anonymousSeller),
                }}
              />
            )}
          </LabelM>
        </Banner>
      )}
      <StyledTabBar fullWidth forceMobileLayout value={paymentCurrency}>
        {[
          {
            value: Currency.FIAT,
            disabled: !fiatPaymentEnabled,
            label: (
              <Horizontal as="span">
                <FontAwesomeIcon
                  size="lg"
                  icon={getFaCurrencySymbol(fiatCurrency)}
                />
                <LabelL as="span" bold uppercase>
                  {fiatCurrency}
                </LabelL>
              </Horizontal>
            ),
          },
          {
            value: Currency.ETH,
            disabled: !currencies.includes(Currency.ETH),
            label: (
              <Horizontal as="span">
                <FontAwesomeIcon size="lg" icon={faEthereum} />
                <LabelL as="span" bold uppercase>
                  ETH
                </LabelL>
              </Horizontal>
            ),
          },
        ].map(t => {
          return (
            <Tab
              key={t.value}
              value={t.value}
              disabled={t.disabled}
              onClick={() => setPaymentCurrency(t.value)}
            >
              {t.label}
            </Tab>
          );
        })}
      </StyledTabBar>
    </Vertical>
  );
};

export default CurrencyTabs;
